/**
 * Hook for tracking total unread message count
 * Uses unified ChatRealtimeManager for efficient connection sharing
 * 
 * IMPORTANT: Uses useAuth() hook to read cached auth state.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useGlobalChatRealtime } from './useChatRealtime';
import { useAuth } from './useAuth';

export function useUnreadCount() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Read user from cached auth state — no network call!
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const fetchUnreadCount = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    const { data, error } = await supabase.rpc('fn_chat_unread_total', {
      p_user_id: userId,
    });
    if (!error && typeof data === 'number') {
      setUnreadCount(data);
    }
    setIsLoading(false);
  }, [userId]);

  // Fetch on mount and when user changes
  useEffect(() => {
    if (userId) {
      fetchUnreadCount();
    } else {
      setUnreadCount(0);
      setIsLoading(false);
    }
  }, [userId, fetchUnreadCount]);

  // Use unified global realtime channel
  useGlobalChatRealtime({
    userId: userId || '',
    onNewMessage: () => {
      setUnreadCount(prev => prev + 1);
    },
    onParticipantUpdate: () => {
      fetchUnreadCount();
    },
  });

  return {
    unreadCount,
    isLoading,
    refresh: fetchUnreadCount,
  };
}

export default useUnreadCount;
